import { DT, DS2 } from '../theme/designTokens';
import { PageContent } from '../components/ui/PageContent';
import { Reveal } from '../components/ui/Reveal';
import { Footer } from '../components/shell/Footer';
import { AtmosphericGlow } from '../components/ui/AtmosphericGlow';
import { DS2Button } from '../components/ui/DS2Button';
import { NumberedFeatureCard } from '../components/ui/NumberedFeatureCard';
import { GradientText } from '../components/ui/GradientText';
import { ASSETS } from '../config/assets';
import { useTranslation } from '../i18n/react';

/**
 * HeroImage — approved Figma marketing photo (distinct from ASSETS.board.*,
 * which is the in-game render). Two variants: hero-board-1 (empty branded
 * table, used in the Hero) and hero-board-2 (populated gameplay scene,
 * confirmed larger per Figma, used in Rise/Compete/Connect).
 */
function HeroImage({ src, maxWidth, className }: { src: string; maxWidth: number; className?: string }) {
  return (
    <div className={className} style={{ width: '100%', maxWidth, position: 'relative' }}>
      <img
        src={src}
        alt=""
        style={{
          display:      'block',
          width:        '100%',
          height:       'auto',
          aspectRatio:  '1 / 1',
          objectFit:    'cover',
          borderRadius: `clamp(16px, ${DS2.spacing.heroImageRadius.pref}vw, ${DS2.spacing.heroImageRadius.max}px)`,
          filter:       'drop-shadow(0 24px 48px rgba(0,0,0,0.60))',
        }}
        draggable={false}
      />
    </div>
  );
}

// ── Landing page content (shared between mobile-flow and desktop-sidebar wrappers) ──
function LandingContent() {
  const { t } = useTranslation('auth');
  const { t: tc } = useTranslation('common');
  return (
    // flex:1;minHeight:0;column mirrors AppShell's wrapper one level further so
    // this screen fills the available viewport height (see AppShell.tsx) —
    // without it, a short page leaves blank shell background below Footer
    // instead of Footer's marginTop:'auto' pushing it flush to the bottom.
    <div style={{ width: '100%', background: DT.color.pageBg, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>

      {/* ── HERO ──────────────────────────────────────────────────────── */}
      {/* No overflow:hidden — it sliced the glow with a hard edge 101px above
          its natural fade; Figma lets glows bleed across section boundaries. */}
      <div style={{ position: 'relative', paddingTop: 37, paddingBottom: 37 }}>
        {/* Figma 1920-export glow geometry (canvas offsets recomputed against
            the content area): Gradient+Blur blocks of the "test" frame. */}
        <AtmosphericGlow width={837.03} height={532.66} top={123.42} leftOffset={-114.3} />
        <PageContent maxWidth={DS2.layout.contentMaxWidth}>
          <div style={{ display: 'flex', gap: 33, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 300px', minWidth: 0, display: 'flex', justifyContent: 'center' }}>
              <HeroImage src={ASSETS.marketing.HERO_BOARD_1} maxWidth={363} className="land-board" />
            </div>
            <div style={{ flex: '1 1 300px', minWidth: 0 }}>
              <h1
                className="land-text-in"
                style={{
                  fontSize:      `clamp(2.17rem, ${DS2.type.heroHeading.pref}vw, ${DS2.type.heroHeading.max}px)`,
                  lineHeight:    1.05,
                  letterSpacing: '-0.02em',
                  fontWeight:    700,
                  fontFamily:    DS2.font.heading,
                  color:         '#FFFFFF',
                  margin:        0,
                }}
              >
                {/* Two keys, not one sentence split at render: the line
                    break + gold accent is structural, so each line is its
                    own complete translatable unit. */}
                {t('landing.heroTitle')}<br />
                <GradientText>{t('landing.heroTitleAccent')}</GradientText>
              </h1>
              <p
                className="land-text-in"
                style={{
                  fontSize:       `clamp(0.93rem, ${DS2.type.paragraph.pref}vw, ${DS2.type.paragraph.max}px)`,
                  lineHeight:     DS2.type.paragraph.lineHeight,
                  fontFamily:     DS2.font.body,
                  fontWeight:     300,
                  color:          DS2.color.textBright,
                  margin:         '19px 0 0',
                  maxWidth:       900,
                  animationDelay: '150ms',
                }}
              >
                {t('landing.heroBody')}
              </p>
              <div
                className="land-text-in"
                style={{ display: 'flex', gap: `clamp(13px, ${DS2.spacing.ctaGap.pref}vw, ${DS2.spacing.ctaGap.max}px)`, marginTop: 25, flexWrap: 'wrap', animationDelay: '300ms' }}
              >
                <DS2Button to="/signup" variant="gold" shine>{t('landing.ctaFree')}</DS2Button>
                <DS2Button to="/login" variant="ghost">{tc('action.logIn')}</DS2Button>
              </div>
            </div>
          </div>
        </PageContent>
      </div>

      <div style={{ height: 1, background: DS2.color.divider }} />

      {/* ── WHY PLAY HERE ─────────────────────────────────────────────── */}
      <div style={{ position: 'relative', paddingTop: 56, paddingBottom: 56 }}>
        <AtmosphericGlow width={908.26} height={532.66} top={-16.46} leftOffset={-49.2} />
        <PageContent maxWidth={DS2.layout.contentMaxWidth}>
          <Reveal>
            <div style={{ fontSize: `clamp(1rem, ${DS2.type.eyebrow.pref}vw, ${DS2.type.eyebrow.max}px)`, fontWeight: DS2.type.eyebrow.weight, fontFamily: DS2.font.body, color: DS2.color.gold, letterSpacing: '0.158em', textTransform: 'uppercase' }}>
              {t('landing.whyEyebrow')}
            </div>
            <h2
              style={{
                fontSize:      `clamp(1.67rem, ${DS2.type.sectionHeading.pref}vw, ${DS2.type.sectionHeading.max}px)`,
                lineHeight:    DS2.type.sectionHeading.lineHeight,
                letterSpacing: '-0.02em',
                fontWeight:    700,
                fontFamily:    DS2.font.heading,
                color:         DT.color.textPrimary,
                margin:        '12px 0 0',
              }}
            >
              {t('landing.whyTitle')} <GradientText>{t('landing.whyTitleAccent')}</GradientText>
            </h2>
          </Reveal>

          <div style={{ display: 'grid', gap: DS2.spacing.cardGap, marginTop: 50 }} className="md:grid-cols-3">
            <Reveal delay={0}>
              <NumberedFeatureCard number="01" title={t('landing.card1Title')}
                body={t('landing.card1Body')} />
            </Reveal>
            <Reveal delay={100}>
              <NumberedFeatureCard number="02" title={t('landing.card2Title')}
                body={t('landing.card2Body')} />
            </Reveal>
            <Reveal delay={200}>
              <NumberedFeatureCard number="03" title={t('landing.card3Title')}
                body={t('landing.card3Body')} />
            </Reveal>
          </div>
        </PageContent>
      </div>

      <div style={{ height: 1, background: DS2.color.divider }} />

      {/* ── RISE. COMPETE. CONNECT. ──────────────────────────────────── */}
      <div style={{ position: 'relative', paddingTop: 56, paddingBottom: 56 }}>
        <AtmosphericGlow width={700.3} height={556.65} top={136.97} leftOffset={100.4} />
        <PageContent maxWidth={DS2.layout.contentMaxWidth}>
          <Reveal>
            <div style={{ display: 'flex', gap: 33, alignItems: 'center', flexWrap: 'wrap-reverse' }}>
              <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                <h2
                  style={{
                    fontSize:      `clamp(1.67rem, ${DS2.type.sectionHeading.pref}vw, ${DS2.type.sectionHeading.max}px)`,
                    lineHeight:    DS2.type.sectionHeading.lineHeight,
                    letterSpacing: '-0.02em',
                    fontWeight:    700,
                    fontFamily:    DS2.font.heading,
                    color:         '#FFFFFF',
                    margin:        0,
                  }}
                >
                  {t('landing.riseTitle1')} <GradientText>{t('landing.riseTitleAccent')}</GradientText><br />{t('landing.riseTitle2')}
                </h2>
                <p
                  style={{
                    fontSize:   `clamp(0.93rem, ${DS2.type.paragraph.pref}vw, ${DS2.type.paragraph.max}px)`,
                    lineHeight: DS2.type.paragraph.lineHeight,
                    color:      DS2.color.textBright,
                    fontFamily: DS2.font.body,
                    margin:     '16px 0 0',
                    maxWidth:   860,
                  }}
                >
                  {t('landing.riseBody')}
                </p>
                <div style={{ marginTop: 25 }}>
                  <DS2Button to="/signup" variant="gold">{t('landing.ctaFree')}</DS2Button>
                </div>
              </div>
              <div style={{ flex: '1 1 280px', minWidth: 0, display: 'flex', justifyContent: 'center' }}>
                <HeroImage src={ASSETS.marketing.HERO_BOARD_2} maxWidth={397} />
              </div>
            </div>
          </Reveal>
        </PageContent>
      </div>

      <Footer />
    </div>
  );
}

// ── Landing screen ──────────────────────────────────────────────────────────
//
// Pure content — no shell/nav logic here. AppShell decides whether this
// route renders the legacy TopNav or the new DS2 Sidebar (see
// SIDEBAR_SHELL_ROUTES in AppShell.tsx). Landing is the first route on
// that list, making it the first consumer of the new shell.

export function LandingScreen() {
  return <LandingContent />;
}
